/**
 * AddItemForm - Formulario inline para anadir items
 *
 * Permite agregar rapidamente nuevos items a un checklist
 * con categoria y prioridad opcionales.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useChecklistStore } from '@/store';
import type { ItemCategory } from '@/core/types';
import { Button } from '@/ui/primitives';
import { cn } from '@/core/utils';

interface AddItemFormProps {
  checklistId: string;
}

const CATEGORIES: { value: ItemCategory; label: string }[] = [
  { value: 'health', label: 'Salud' },
  { value: 'personal', label: 'Personal' },
  { value: 'tech', label: 'Tech' },
  { value: 'hygiene', label: 'Higiene' },
  { value: 'clothing', label: 'Ropa' },
  { value: 'work', label: 'Trabajo' },
  { value: 'other', label: 'Otros' },
];

export function AddItemForm({ checklistId }: AddItemFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<ItemCategory>('other');
  const [isCritical, setIsCritical] = useState(false);

  const addItem = useChecklistStore((state) => state.addItem);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;

    addItem(checklistId, {
      label: label.trim(),
      category,
      priority: isCritical ? 'critical' : 'normal',
    });

    setLabel('');
    setIsCritical(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-300 transition-colors"
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm">Anadir item</span>
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-slate-900 rounded-xl p-4 space-y-3"
    >
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Nombre del item..."
        autoFocus
        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            type="button"
            onClick={() => setCategory(cat.value)}
            className={cn(
              'px-2 py-1 rounded text-xs transition-colors',
              category === cat.value
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-slate-200'
            )}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isCritical}
            onChange={(e) => setIsCritical(e.target.checked)}
            className="rounded border-slate-600 bg-slate-800 text-rose-500 focus:ring-rose-500"
          />
          <span className="text-sm text-slate-400">Item critico</span>
        </label>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(false)}
          >
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={!label.trim()}>
            Anadir
          </Button>
        </div>
      </div>
    </form>
  );
}
